import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehicleAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import './AdminVehicles.css';

const AdminVehicles = () => {
    const navigate = useNavigate();
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [selectedVehicleForImages, setSelectedVehicleForImages] = useState(null);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [formData, setFormData] = useState({
        name_tr: '',
        name_en: '',
        name_ru: '',
        name_ar: '',
        description_tr: '',
        description_en: '',
        description_ru: '',
        description_ar: '',
        hourly_price: '',
        daily_price: '',
        is_active: 1
    });

    useEffect(() => {
        fetchVehicles();
    }, []);

    const fetchVehicles = async () => {
        try {
            const response = await vehicleAPI.getAllAdmin();
            setVehicles(response.data.data);
        } catch (error) {
            console.error('Fetch vehicles error:', error);
            toast.error('Araçlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin/login');
    };

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? (e.target.checked ? 1 : 0) : e.target.value;
        setFormData({
            ...formData,
            [e.target.name]: value
        });
    };

    const handleOpenModal = (vehicle = null) => {
        if (vehicle) {
            setEditingVehicle(vehicle);
            setFormData({
                name_tr: vehicle.name_tr,
                name_en: vehicle.name_en,
                name_ru: vehicle.name_ru || vehicle.name_en,
                name_ar: vehicle.name_ar || vehicle.name_en,
                description_tr: vehicle.description_tr || '',
                description_en: vehicle.description_en || '',
                description_ru: vehicle.description_ru || '',
                description_ar: vehicle.description_ar || '',
                hourly_price: vehicle.hourly_price,
                daily_price: vehicle.daily_price || '', // null ise boş string
                is_active: vehicle.is_active
            });
        } else {
            setEditingVehicle(null);
            setFormData({
                name_tr: '',
                name_en: '',
                name_ru: '',
                name_ar: '',
                description_tr: '',
                description_en: '',
                description_ru: '',
                description_ar: '',
                hourly_price: '',
                daily_price: '',
                is_active: 1
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingVehicle(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name_tr || !formData.name_en) {
            toast.error('Türkçe ad ve İngilizce ad gerekli');
            return;
        }

        try {
            // Gönderilecek veriyi hazırla - fiyatları 0 gönder
            const dataToSend = {
                ...formData,
                hourly_price: 0,
                daily_price: 0
            };



            if (editingVehicle) {
                await vehicleAPI.update(editingVehicle.id, dataToSend);
                toast.success('Araç güncellendi');
            } else {
                await vehicleAPI.create(dataToSend);
                toast.success('Araç eklendi');
            }
            handleCloseModal();
            fetchVehicles();
        } catch (error) {
            console.error('Save vehicle error:', error);
            toast.error('İşlem başarısız');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu aracı silmek istediğinizden emin misiniz?')) {
            return;
        }

        try {
            await vehicleAPI.delete(id);
            toast.success('Araç silindi');
            fetchVehicles();
        } catch (error) {
            console.error('Delete vehicle error:', error);
            toast.error('Silme işlemi başarısız');
        }
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;

        // Eğer imagePath zaten tam URL ise, direkt kullan
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }

        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://elittaksibodrum.com';

        // Eğer path /api ile başlamıyorsa, /api ekle
        if (!imagePath.startsWith('/api/')) {
            // /uploads/vehicles/... → /api/uploads/vehicles/...
            if (imagePath.startsWith('/uploads/')) {
                return `${baseUrl}/api${imagePath}`;
            }
            // Diğer durumlar için /api ekle
            return `${baseUrl}/api${imagePath}`;
        }

        // Zaten /api ile başlıyorsa, direkt ekle
        return `${baseUrl}${imagePath}`;
    };

    const handleOpenImageModal = (vehicle) => {
        setSelectedVehicleForImages(vehicle);
        setShowImageModal(true);
    };

    const handleCloseImageModal = () => {
        setShowImageModal(false);
        setSelectedVehicleForImages(null);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            await handleImageUpload(e.dataTransfer.files);
        }
    };

    const handleFileSelect = async (e) => {
        if (e.target.files && e.target.files.length > 0) {
            await handleImageUpload(e.target.files);
        }
    };

    const handleImageUpload = async (files) => {
        if (!selectedVehicleForImages) return;

        const formData = new FormData();
        Array.from(files).forEach(file => {
            if (file.type.startsWith('image/')) {
                formData.append('images', file);
            }
        });

        try {
            setUploadingImages(true);
            await vehicleAPI.uploadImages(selectedVehicleForImages.id, formData);
            toast.success('Resimler yüklendi!');
            fetchVehicles();

            // Güncel araç bilgisini al
            const updatedVehicles = await vehicleAPI.getAllAdmin();
            const updatedVehicle = updatedVehicles.data.data.find(v => v.id === selectedVehicleForImages.id);
            setSelectedVehicleForImages(updatedVehicle);
        } catch (error) {
            console.error('Image upload error:', error);
            toast.error('Resim yükleme hatası');
        } finally {
            setUploadingImages(false);
        }
    };

    const handleDeleteImage = async (imagePath) => {
        if (!window.confirm('Bu resmi silmek istediğinizden emin misiniz?')) {
            return;
        }

        try {
            await vehicleAPI.deleteImage(selectedVehicleForImages.id, imagePath);
            toast.success('Resim silindi');
            fetchVehicles();

            // Güncel araç bilgisini al
            const updatedVehicles = await vehicleAPI.getAllAdmin();
            const updatedVehicle = updatedVehicles.data.data.find(v => v.id === selectedVehicleForImages.id);
            setSelectedVehicleForImages(updatedVehicle);
        } catch (error) {
            console.error('Delete image error:', error);
            toast.error('Resim silme hatası');
        }
    };

    if (loading) {
        return (
            <div className="admin-page">
                <div className="loading">Yükleniyor...</div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <div>
                    <button onClick={() => navigate('/admin/dashboard')} className="btn-back">
                        ← Geri
                    </button>
                    <h1>🚕 Araç Yönetimi</h1>
                </div>
                <button onClick={handleLogout} className="btn-logout">
                    Çıkış Yap
                </button>
            </div>

            <div className="admin-content">
                <div className="content-header">
                    <h2>Araçlar ({vehicles.length})</h2>
                    <button onClick={() => handleOpenModal()} className="btn-primary">
                        + Yeni Araç Ekle
                    </button>
                </div>

                <div className="vehicles-table">
                    {vehicles.length === 0 ? (
                        <p className="no-data">Henüz araç eklenmemiş</p>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th>Resim</th>
                                    <th>Türkçe Ad</th>
                                    <th>İngilizce Ad</th>
                                    <th>Durum</th>
                                    <th>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicles.map(vehicle => (
                                    <tr key={vehicle.id}>
                                        <td>
                                            {vehicle.images && vehicle.images.length > 0 ? (
                                                <img
                                                    src={getImageUrl(vehicle.images[0])}
                                                    alt={vehicle.name_tr}
                                                    className="vehicle-thumb"
                                                />
                                            ) : (
                                                <div className="no-image">🚕</div>
                                            )}
                                        </td>
                                        <td>{vehicle.name_tr}</td>
                                        <td>{vehicle.name_en}</td>
                                        <td>
                                            <span className={`status-badge ${vehicle.is_active ? 'active' : 'inactive'}`}>
                                                {vehicle.is_active ? 'Aktif' : 'Pasif'}
                                            </span>
                                        </td>
                                        <td className="actions">
                                            <button
                                                onClick={() => handleOpenImageModal(vehicle)}
                                                className="btn-images"
                                                title="Resimleri Yönet"
                                            >
                                                🖼️ Resimler ({vehicle.images?.length || 0})
                                            </button>
                                            <button
                                                onClick={() => handleOpenModal(vehicle)}
                                                className="btn-edit"
                                            >
                                                Düzenle
                                            </button>
                                            <button
                                                onClick={() => handleDelete(vehicle.id)}
                                                className="btn-delete"
                                            >
                                                Sil
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingVehicle ? 'Araç Düzenle' : 'Yeni Araç Ekle'}</h2>
                            <button onClick={handleCloseModal} className="btn-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="vehicle-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Türkçe Ad *</label>
                                    <input
                                        type="text"
                                        name="name_tr"
                                        value={formData.name_tr}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>İngilizce Ad *</label>
                                    <input
                                        type="text"
                                        name="name_en"
                                        value={formData.name_en}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Rusça Ad</label>
                                    <input
                                        type="text"
                                        name="name_ru"
                                        value={formData.name_ru}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Arapça Ad</label>
                                    <input
                                        type="text"
                                        name="name_ar"
                                        value={formData.name_ar}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Türkçe Açıklama</label>
                                <textarea
                                    name="description_tr"
                                    value={formData.description_tr}
                                    onChange={handleChange}
                                    rows="3"
                                />
                            </div>

                            <div className="form-group">
                                <label>İngilizce Açıklama</label>
                                <textarea
                                    name="description_en"
                                    value={formData.description_en}
                                    onChange={handleChange}
                                    rows="3"
                                />
                            </div>

                            <div className="form-group">
                                <label>Rusça Açıklama</label>
                                <textarea
                                    name="description_ru"
                                    value={formData.description_ru}
                                    onChange={handleChange}
                                    rows="3"
                                />
                            </div>

                            <div className="form-group">
                                <label>Arapça Açıklama</label>
                                <textarea
                                    name="description_ar"
                                    value={formData.description_ar}
                                    onChange={handleChange}
                                    rows="3"
                                />
                            </div>



                            <div className="form-row">
                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={formData.is_active === 1}
                                            onChange={handleChange}
                                        />
                                        Aktif
                                    </label>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" onClick={handleCloseModal} className="btn-cancel">
                                    İptal
                                </button>
                                <button type="submit" className="btn-save">
                                    {editingVehicle ? 'Güncelle' : 'Ekle'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Resim Yönetimi Modal */}
            {showImageModal && selectedVehicleForImages && (
                <div className="modal-overlay" onClick={handleCloseImageModal}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>🖼️ Resim Yönetimi - {selectedVehicleForImages.name_tr}</h2>
                            <button onClick={handleCloseImageModal} className="btn-close">×</button>
                        </div>

                        <div className="image-manager">
                            {/* Resim Yükleme Alanı */}
                            <div
                                className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    id="file-upload"
                                    multiple
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                    disabled={uploadingImages}
                                />
                                <label htmlFor="file-upload" className="upload-label">
                                    {uploadingImages ? (
                                        <>
                                            <div className="upload-spinner"></div>
                                            <p>Yükleniyor...</p>
                                        </>
                                    ) : (
                                        <>
                                            <div className="upload-icon">📤</div>
                                            <p className="upload-text">Resimleri sürükleyip bırakın</p>
                                            <p className="upload-subtext">veya tıklayarak seçin</p>
                                            <span className="upload-hint">JPG, PNG, WebP (Max 10MB)</span>
                                        </>
                                    )}
                                </label>
                            </div>

                            {/* Mevcut Resimler */}
                            <div className="images-grid">
                                {selectedVehicleForImages.images && selectedVehicleForImages.images.length > 0 ? (
                                    selectedVehicleForImages.images.map((imagePath, index) => (
                                        <div key={index} className="image-item">
                                            <img
                                                src={getImageUrl(imagePath)}
                                                alt={`${selectedVehicleForImages.name_tr} ${index + 1}`}
                                                className="image-preview"
                                            />
                                            <button
                                                onClick={() => handleDeleteImage(imagePath)}
                                                className="btn-delete-image"
                                                title="Resmi Sil"
                                            >
                                                🗑️
                                            </button>
                                            {index === 0 && (
                                                <span className="primary-badge">Ana Resim</span>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-images">
                                        <p>🚕</p>
                                        <p>Henüz resim eklenmemiş</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button onClick={handleCloseImageModal} className="btn-save">
                                Tamam
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminVehicles;